import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import ToggleSwitch from '../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import { AddressPageProps } from '../../../../../../@types/props-types'
import { SCREEN_ADDRESS, SCREENS } from '../../../../../../services/domain/wizardScreens'
import { AddressScreenValidationResult } from '../../../../../../@types/validation-types'
import { TextInput } from '../../../../../common/inputs/textInput/TextInput'
import { InitState } from '../../../../../../redux/reducers/root/initialState'
import {
  loadCountries,
  loadRegionsOfCountry,
} from '../../../../../../redux/thunks/internationalAddress/internationalAddress'
import DropdownSelect from '../../../../../common/inputs/dropdownSelect/DropdownSelect'
import { DropdownOptionSelection } from '../../../../../../@types/types'
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks'
import { isGermanCountryCode } from '../../../../../../utils/geo-utils'
import { isWebView } from '../../../../../../utils/navigation'
import Checkbox from '../../../../../common/inputs/checkbox/Checkbox'
import LabelWithTooltip from '../../basicInfo/fields/common/LabelWithTooltip'
import AddressPopoverContent from '../addressPopoverContent/AddressPopoverContent'
import { validateCurrentInsertion } from '../../../../../../redux/thunks/insertionData/insertionData'

const screen = SCREENS[SCREEN_ADDRESS]

const InternationalAddress: React.FC<AddressPageProps<AddressScreenValidationResult>> = ({
  insertionData,
  validationMessages,
  onChange,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { manualGeoCoding } = useAppSelector((initial: InitState) => initial.addressFeature)
  const submitCount = useAppSelector((initial: InitState) => initial.formStateFeature.submitCount)
  const isWebViewEnabled = isWebView()
  const manualCountryValue = insertionData.manualCountry?.value
  const manualCountryLabel = insertionData.manualCountry?.label

  // This is a workaround to make sure that the manualCountry and manualRegion fields are validated
  // if the validation has been previously triggered
  useEffect(() => {
    if (submitCount > 0) {
      dispatch(validateCurrentInsertion())
    }
  }, [dispatch, submitCount])

  useEffect(() => {
    !manualGeoCoding.countries.length && dispatch(loadCountries())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (manualCountryValue) {
      dispatch(loadRegionsOfCountry({ value: manualCountryValue, label: manualCountryLabel ?? null }))
    }
  }, [dispatch, manualCountryLabel, manualCountryValue])

  const streetLabelWithTooltip = (
    <LabelWithTooltip
      inputId={'street'}
      labelText={t('wizard.screen.internationalAddress.street')}
      tooltipContent={<AddressPopoverContent />}
      showMandatory={false}
    />
  )

  return (
    <>
      <div className='grid-item one-whole margin-bottom-l pii-hide'>
        <DropdownSelect
          id='country'
          name='manualCountry'
          placeholder={t('wizard.screen.internationalAddress.countryPlaceholder', 'No selection')}
          label='wizard.screen.internationalAddress.country'
          value={insertionData.manualCountry?.value}
          options={manualGeoCoding.countries.filter((country) => !isGermanCountryCode(country.value))}
          onChange={(value: DropdownOptionSelection) => {
            onChange({ screen: screen.name, property: 'manualCountry' }, value)
          }}
          errors={validationMessages?.manualCountry ?? []}
        />
      </div>
      <div className='grid-item one-whole margin-bottom-l pii-hide'>
        <DropdownSelect
          id='region'
          name='manualRegion'
          placeholder={t('wizard.screen.internationalAddress.regionPlaceholder', 'No selection')}
          label='wizard.screen.internationalAddress.region'
          value={insertionData.manualRegion?.value}
          options={manualGeoCoding.regions}
          onChange={(value: DropdownOptionSelection) => {
            onChange({ screen: screen.name, property: 'manualRegion' }, value)
          }}
          errors={validationMessages?.manualRegion ?? []}
        />
      </div>
      <div className={`grid-item ${isWebViewEnabled ? 'palm-one-whole' : 'nine-twelfths'}`}>
        <TextInput
          id='street'
          propertyName='street'
          label={streetLabelWithTooltip}
          value={insertionData.street}
          type='text'
          errors={validationMessages!.street ?? []}
          doOnFieldChange={(property, value) => onChange({ screen: screen.name, property }, value)}
          className={'pii-hide'}
          dataHeyimmoContext='address_street'
        />
      </div>
      <div className={`grid-item ${isWebViewEnabled ? 'palm-one-whole margin-top-l' : 'two-twelfth push-one-twelfth'}`}>
        <TextInput
          id='house-number'
          propertyName='houseNumber'
          label={
            <>
              {isWebViewEnabled
                ? t('wizard.screen.internationalAddress.mobile.houseNumber', 'Hausnummer*')
                : t('wizard.screen.nationalAddressinternationalAddress.houseNumber', 'Nr.')}
            </>
          }
          value={insertionData.houseNumber}
          type='text'
          errors={validationMessages!.houseNumber ?? []}
          doOnFieldChange={(property, value) => onChange({ screen: screen.name, property }, value)}
          className={'pii-hide'}
          dataHeyimmoContext='address_house_number'
        />
      </div>
      {!isWebViewEnabled && (
        <div className='grid-item one-whole margin-top-s'>
          <ToggleSwitch
            id='address-visibility'
            alignment='right'
            propertyName='hideStreetAndStreetNumber'
            label={<span>{t('wizard.screen.address.visibility', 'Visible to interested people')}</span>}
            value={insertionData.hideStreetAndStreetNumber ?? false}
            onChange={(property, value) => onChange({ screen: screen.name, property }, value)}
          />
        </div>
      )}
      <div className={`grid-item margin-top-l ${isWebViewEnabled ? 'palm-one-whole' : 'three-twelfths'}`}>
        <TextInput
          id='postcode'
          propertyName='postcode'
          label={
            <span>
              {isWebViewEnabled
                ? t('wizard.screen.internationalAddress.mobile.postcode', 'Postleitzahl')
                : t('wizard.screen.internationalAddress.postcode', 'Postcode')}
            </span>
          }
          value={insertionData.postcode}
          type='text'
          errors={validationMessages!.postcode ?? []}
          maxLength={12}
          doOnFieldChange={(property, value) => onChange({ screen: screen.name, property }, value)}
          className={'pii-hide'}
          dataHeyimmoContext='address_postcode'
        />
      </div>
      <div
        className={`grid-item margin-top-l ${isWebViewEnabled ? 'palm-one-whole' : 'eight-twelfths push-one-twelfth'}`}
      >
        <TextInput
          id='city'
          propertyName='city'
          maxLength={100}
          label={<span>{t('wizard.screen.address.city', 'City*')}</span>}
          value={insertionData.city}
          type='text'
          errors={validationMessages!.city ?? []}
          doOnFieldChange={(property, value) => onChange({ screen: screen.name, property }, value)}
          className={'pii-hide'}
          dataHeyimmoContext='address_city'
        />
      </div>
      {isWebViewEnabled && (
        <div className='grid-item one-whole margin-top-s'>
          <Checkbox
            id='show-whole-address'
            onChange={(_name, checked) => onChange({ screen: screen.name, property: 'showWholeAddress' }, checked)}
            name='showWholeAddress'
            checked={insertionData.showWholeAddress ?? false}
            label={<span>{t('wizard.screen.address.showWholeAddress', 'unknown')}</span>}
          />
        </div>
      )}
    </>
  )
}

export default InternationalAddress
